<script setup>
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  policy: { type: Object, required: true },
  actionPolicyItems: { type: Array, default: () => [] },
  actionInstructions: { type: Object, required: true },
  storeName: { type: String, default: '' },
  catalogResultLimit: { type: Number, default: 10 },
  catalogImagesOnly: { type: Boolean, default: false },
  metaCatalogEnabled: { type: Boolean, default: false },
  metaCatalogId: { type: String, default: '' },
  metaCatalogToken: { type: String, default: '' },
  metaCatalogTokenConfigured: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:storeName',
  'update:catalogResultLimit',
  'update:catalogImagesOnly',
  'update:metaCatalogEnabled',
  'update:metaCatalogId',
  'update:metaCatalogToken',
  'update:policy',
  'update:actionInstructions',
  'save',
]);

const { t } = useI18n();

const updatePolicy = (key, value) =>
  emit('update:policy', { ...props.policy, [key]: value });

const updateActionInstruction = (key, value) =>
  emit('update:actionInstructions', {
    ...props.actionInstructions,
    [key]: value,
  });
</script>

<template>
  <div class="flex flex-col gap-6">
    <div
      class="bg-white dark:bg-n-solid-2 p-6 rounded-xl shadow-sm border border-n-strong"
    >
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('LUMERAI_SETTINGS.SHOPIFY.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mb-6">
        {{ t('LUMERAI_SETTINGS.SHOPIFY.DESCRIPTION') }}
      </p>

      <div class="p-5 mb-4 bg-n-alpha-1 border border-n-strong rounded-xl">
        <label
          class="block font-medium text-n-slate-12 mb-1"
          for="shopify-store-name"
          >{{ t('LUMERAI_SETTINGS.SHOPIFY.STORE_NAME') }}</label
        >
        <input
          id="shopify-store-name"
          :value="storeName"
          type="text"
          maxlength="80"
          :placeholder="t('LUMERAI_SETTINGS.SHOPIFY.STORE_NAME_PLACEHOLDER')"
          class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
          @input="emit('update:storeName', $event.target.value.trim())"
        />
        <p class="text-xs text-n-slate-11 mt-2">
          {{ t('LUMERAI_SETTINGS.SHOPIFY.STORE_NAME_HELP') }}
        </p>
      </div>

      <div class="p-5 mb-4 bg-n-alpha-1 border border-n-strong rounded-xl">
        <div class="flex items-start justify-between gap-4">
          <div>
            <h4 class="font-medium text-n-slate-12 m-0">
              {{ t('LUMERAI_SETTINGS.SHOPIFY.CATALOG_TITLE') }}
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mt-1">
              {{ t('LUMERAI_SETTINGS.SHOPIFY.CATALOG_DESCRIPTION') }}
            </p>
          </div>
          <Switch
            :model-value="props.policy.shopify_catalog_enabled"
            :aria-label="t('LUMERAI_SETTINGS.SHOPIFY.CATALOG_TOGGLE')"
            @update:model-value="
              updatePolicy('shopify_catalog_enabled', $event)
            "
          />
        </div>
        <div v-if="props.policy.shopify_catalog_enabled">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div
              class="p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
            >
              <label
                class="block font-medium text-n-slate-12 mb-1"
                for="shopify-catalog-result-limit"
                >{{ t('LUMERAI_SETTINGS.SHOPIFY.PRODUCTS_PER_SEARCH') }}</label
              >
              <p class="text-sm text-n-slate-11 m-0 mb-3">
                {{ t('LUMERAI_SETTINGS.SHOPIFY.PRODUCTS_PER_SEARCH_HELP') }}
              </p>
              <input
                id="shopify-catalog-result-limit"
                :value="catalogResultLimit"
                type="number"
                min="1"
                max="10"
                step="1"
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="
                  emit('update:catalogResultLimit', Number($event.target.value))
                "
              />
            </div>
            <div
              class="flex items-center justify-between gap-4 p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
            >
              <div>
                <h4 class="font-medium text-n-slate-12 m-0">
                  {{ t('LUMERAI_SETTINGS.SHOPIFY.IMAGES_ONLY') }}
                </h4>
                <p class="text-sm text-n-slate-11 m-0 mt-1">
                  {{ t('LUMERAI_SETTINGS.SHOPIFY.IMAGES_ONLY_HELP') }}
                </p>
              </div>
              <Switch
                :model-value="catalogImagesOnly"
                :aria-label="t('LUMERAI_SETTINGS.SHOPIFY.IMAGES_ONLY_TOGGLE')"
                @update:model-value="emit('update:catalogImagesOnly', $event)"
              />
            </div>
          </div>
          <div class="mt-4">
            <h4 class="font-medium text-n-slate-12 m-0 mb-1">
              {{ t('LUMERAI_SETTINGS.SHOPIFY.WHEN_TO_SEARCH') }}
            </h4>
            <p class="text-sm text-n-slate-11 m-0 mb-3">
              {{ t('LUMERAI_SETTINGS.SHOPIFY.SEARCH_INSTRUCTION_HELP') }}
            </p>
            <TextArea
              :model-value="props.actionInstructions.shopify_catalog_enabled"
              :placeholder="
                t('LUMERAI_SETTINGS.SHOPIFY.SEARCH_INSTRUCTION_PLACEHOLDER')
              "
              :rows="4"
              :max-length="600"
              @update:model-value="
                updateActionInstruction('shopify_catalog_enabled', $event)
              "
            />
          </div>
          <div
            class="mt-4 p-4 bg-white dark:bg-n-solid-2 border border-n-strong rounded-xl"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h4 class="font-medium text-n-slate-12 m-0">
                  {{ t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_TITLE') }}
                </h4>
                <p class="text-sm text-n-slate-11 m-0 mt-1">
                  {{ t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_DESCRIPTION') }}
                </p>
              </div>
              <Switch
                :model-value="metaCatalogEnabled"
                :aria-label="t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_TOGGLE')"
                @update:model-value="emit('update:metaCatalogEnabled', $event)"
              />
            </div>
            <div v-if="metaCatalogEnabled" class="mt-4">
              <label
                class="block font-medium text-n-slate-12 mt-4 mb-1"
                for="shopify-meta-catalog-id"
                >{{ t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_ID') }}</label
              >
              <input
                id="shopify-meta-catalog-id"
                :value="metaCatalogId"
                type="text"
                :placeholder="
                  t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_ID_PLACEHOLDER')
                "
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="
                  emit('update:metaCatalogId', $event.target.value.trim())
                "
              />
              <label
                class="block font-medium text-n-slate-12 mt-4 mb-1"
                for="shopify-meta-catalog-token"
                >{{ t('LUMERAI_SETTINGS.SHOPIFY.META_CATALOG_TOKEN') }}</label
              >
              <input
                id="shopify-meta-catalog-token"
                :value="metaCatalogToken"
                type="password"
                autocomplete="new-password"
                :placeholder="
                  t('LUMERAI_SETTINGS.SHOPIFY.META_TOKEN_PLACEHOLDER')
                "
                class="block w-full h-10 px-3 py-2.5 rounded-lg bg-n-alpha-black2 text-sm text-n-slate-12 outline outline-1 outline-n-weak focus:outline-n-brand"
                @input="emit('update:metaCatalogToken', $event.target.value)"
              />
              <p class="text-xs text-n-slate-11 mt-2">
                {{
                  metaCatalogTokenConfigured
                    ? t('LUMERAI_SETTINGS.SHOPIFY.META_TOKEN_CONFIGURED')
                    : t('LUMERAI_SETTINGS.SHOPIFY.META_TOKEN_HELP')
                }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="item in props.actionPolicyItems.filter(
            item => item.key !== 'shopify_catalog_enabled'
          )"
          :key="item.key"
          class="p-5 bg-n-alpha-1 border border-n-strong rounded-xl"
        >
          <div class="flex items-start justify-between gap-4">
            <div>
              <h4 class="font-medium text-n-slate-12 m-0">{{ item.title }}</h4>
              <p class="text-sm text-n-slate-11 m-0 mt-1">
                {{ item.description }}
              </p>
            </div>
            <Switch
              :model-value="props.policy[item.key]"
              :aria-label="
                t('LUMERAI_SETTINGS.SHOPIFY.ENABLE_ACTION', {
                  title: item.title,
                })
              "
              @update:model-value="updatePolicy(item.key, $event)"
            />
          </div>
          <template v-if="props.policy[item.key]">
            <p
              v-if="item.key === 'shopify_checkout_enabled'"
              class="mt-4 text-sm text-n-slate-11"
            >
              {{ t('LUMERAI_SETTINGS.SHOPIFY.CHECKOUT_HELP') }}
            </p>
            <TextArea
              :model-value="props.actionInstructions[item.instructionKey]"
              class="mt-4"
              :label="t('LUMERAI_SETTINGS.SHOPIFY.BEFORE_ACTION_LABEL')"
              :placeholder="item.placeholder"
              :rows="3"
              :max-length="600"
              @update:model-value="
                updateActionInstruction(item.instructionKey, $event)
              "
            />
          </template>
        </div>
      </div>

      <p class="mt-5 text-sm text-n-slate-11">
        {{ t('LUMERAI_SETTINGS.SHOPIFY.SAFETY_NOTICE') }}
      </p>
      <div class="flex justify-end mt-4">
        <Button
          :label="t('LUMERAI_SETTINGS.ACTIONS.SAVE_STORE_PERMISSIONS')"
          color="blue"
          :is-loading="props.isSaving"
          @click="emit('save')"
        />
      </div>
    </div>
  </div>
</template>
